import { message } from "./antdMessageBridge";

/**
 * Safely parses a fetch Response used by a grid export/download flow.
 * - Treats an empty body as "no data" (returns []).
 * - Treats a body containing one of `noDataMarkers` as "no data" (returns []),
 *   even if the response status is not ok (some APIs return an error status
 *   with a "No records found" style message instead of an empty success body).
 * - Otherwise parses the body as JSON and normalizes it to an array.
 * - Throws for any other non-ok response or unparsable body.
 */
export const parseExportResponse = async (response, noDataMarkers = []) => {
    const responseText = await response.text();
    const isNoDataMarker = noDataMarkers.some(
        (marker) => marker && responseText?.includes(marker)
    );

    if (!response.ok && !isNoDataMarker) {
        throw new Error(responseText || "Network response was not ok");
    }

    if (!responseText || isNoDataMarker) {
        return [];
    }

    let result;
    try {
        result = JSON.parse(responseText);
    } catch (e) {
        throw new Error(responseText || "Failed to parse API response.");
    }

    return Array.isArray(result) ? result : [result];
};

/**
 * Runs a common "fetch -> parse -> validate -> export" flow for grid
 * download/export icons across Queue and Report pages.
 *
 * Shows message.warning when there is no data to export, and
 * message.error when the fetch/parse/export step fails.
 *
 * @param {object} options
 * @param {Promise<Response>} options.request - the in-flight fetch() call
 * @param {(data: any[], columns: any[], filename: string) => void} options.exportFn - exportToExcel or exportToCSV
 * @param {any[]} options.columns - columns to pass to exportFn
 * @param {string} options.filename - filename to pass to exportFn
 * @param {string[]} [options.noDataMarkers] - substrings in the response body that indicate "no data"
 * @param {(rows: any[]) => any[]} [options.transform] - optional row transform/mapping applied before the empty check
 * @param {string} [options.noDataMessage]
 * @param {string} [options.errorMessage]
 * @param {() => void} [options.onSettled] - optional callback always run at the end (e.g. setLoading(false))
 */
export const runGridExport = async ({
    request,
    exportFn,
    columns,
    filename,
    noDataMarkers = [],
    transform,
    noDataMessage = "The grid is currently empty. There are no records to export",
    errorMessage = "Failed to export data. Please try again.",
    onSettled,
}) => {
    try {
        const response = await request;
        let rows = await parseExportResponse(response, noDataMarkers);

        if (transform) {
            rows = transform(rows);
        }

        if (!rows || rows.length === 0) {
            message.warning(noDataMessage);
            return;
        }

        exportFn(rows, columns, filename);
    } catch (err) {
        console.error(err);
        message.error(errorMessage);
    } finally {
        if (onSettled) {
            onSettled();
        }
    }
};
